import {
  useState, useRef,
  useEffect, useLayoutEffect,
  useCallback, forwardRef, useImperativeHandle,
} from 'react';
import { useNavigate } from 'react-router-dom';

// ── Arc geometry ──────────────────────────────────────────────────────────────
//
//  Cards are placed on a vertical semicircle — active card at the centre,
//  adjacent cards fan upward and downward along the arc.
//
//  Polar → Cartesian (vertical arc, bulging left):
//    θ  = n * ANGULAR_STEP          (n = signed index offset from active)
//    dx = −R · (1 − cos θ)          (horizontal; always ≤ 0, cards recede left)
//    dy =  R · sin θ                (vertical; +n → down, −n → up)
//
//  Active card (n=0): θ=0 → dx=0, dy=0 → sits at the base position.
//  Adjacent cards fan out vertically and curve left symmetrically.

const ANGULAR_STEP   = Math.PI / 5;   // 36° per step — vertical fan spread
const R_RATIO        = 0.95;           // radius = 95% of container height — inactive cards clip top/bottom
const ACTIVE_Y_RATIO = 0.50;           // active card centre at 50% of panel height (centre)
const CARD_W_RATIO   = 0.80;           // card width  = 80% of container width
const CARD_ASPECT    = 7 / 4;          // fixed 9:7 ratio — height derived from width, not viewport
const BASE_X_RATIO   = 0.44;           // left anchor shifted right so left edge stays fixed

// ── Timing ────────────────────────────────────────────────────────────────────
export const AUTO_MS      = 15_000;
const PAUSE_MS            = 18_000;
const ANIM_DURATION       = 660;         // ms per step
const WHEEL_THROTTLE_MS   = 250;         // post-animation grace period (isAnimatingRef is the primary guard)
const WHEEL_MIN_DELTA     = 20;          // ignore tiny trackpad nudges below this deltaY threshold

// easeInOutQuart — slow start, fast middle, slow landing
function easeInOutQuart(t) {
  return t < 0.5 ? 8 * t ** 4 : 1 - Math.pow(-2 * t + 2, 4) / 2;
}

// ── VisualStackContainer ──────────────────────────────────────────────────────
const VisualStackContainer = forwardRef(function VisualStackContainer(
  { items, onActiveChange, onOpenModal }, ref,
) {
  const routerNav = useNavigate();
  const rootRef   = useRef(null);
  const itemRefs  = useRef([]);
  const posRef    = useRef(0);           // current animated position (fractional)
  const targetRef = useRef(0);           // snap target (integer index)
  const rafRef    = useRef(null);
  const geomRef   = useRef({ W: 0, R: 0 });
  const animRef   = useRef({ from: 0, to: 0, startTime: null });

  const [activeIdx,   setActiveIdx]   = useState(0);
  const [isMobileView, setIsMobileView] = useState(() => window.innerWidth <= 767);

  useEffect(() => {
    const handler = () => setIsMobileView(window.innerWidth <= 767);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isPausedRef    = useRef(false);
  const pauseTimerRef  = useRef(null);
  const autoTimerRef   = useRef(null);
  const lastWheelRef   = useRef(0);
  const isAnimatingRef = useRef(false);  // true while RAF animation is running

  // ── measure container ──────────────────────────────────────────────────────
  const measure = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const W = el.clientWidth;
    const H = el.clientHeight;
    geomRef.current = {
      W, H,
      R:         H * R_RATIO,
      cardBaseH: (W * CARD_W_RATIO) / CARD_ASPECT,
    };
  }, []);

  // ── paint ──────────────────────────────────────────────────────────────────
  // ONLY function that writes transform / opacity / zIndex.
  // These props must be absent from VisualItem's JSX style — React would
  // overwrite them on every re-render, causing the stutter.
  const paint = useCallback(() => {
    const { W, R } = geomRef.current;
    if (!W) return;
    const pos      = posRef.current;
    const len      = itemRefs.current.filter(Boolean).length || 1;
    const isMobile = window.innerWidth <= 767;

    itemRefs.current.forEach((el, i) => {
      if (!el) return;
      const raw     = i - pos;
      const offset  = ((raw % len + len + len / 2) % len) - len / 2;
      const θ       = offset * ANGULAR_STEP;
      const absDist = Math.abs(offset);
      const scale   = Math.max(0.40, 1 - absDist * 0.35);
      const cardH   = Math.max(geomRef.current.cardBaseH * 0.55, geomRef.current.cardBaseH * (1 - absDist * 0.07));

      let opacity, dx, dy;
      if (isMobile) {
        // Peek slide — adjacent cards visible on sides
        const cardW = W * 0.80;
        dx = offset * (cardW + 12);
        dy = 0;
        opacity = Math.max(0, 1 - absDist * 0.4);
      } else {
        // Vertical arc — cards fan up/down (desktop)
        opacity = absDist > 2 ? 0 : Math.max(0, 1 - absDist * 0.55);
        dx = -R * (1 - Math.cos(θ));
        dy =  R * Math.sin(θ);
      }

      const mobileScale = Math.max(0.94, 1 - absDist * 0.06);
      el.style.height    = isMobile ? '100%' : `${cardH}px`;
      el.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(${isMobile ? mobileScale : scale})`;
      el.style.opacity   = opacity;
      el.style.zIndex    = Math.round(20 - absDist * 3);
    });
  }, []);

  // ── first paint before browser draws (no flash of unstyled items) ──────────
  useLayoutEffect(() => {
    measure();
    paint();
  }, [measure, paint]);

  // ── re-measure + repaint on resize ────────────────────────────────────────
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => { measure(); paint(); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, paint]);

  // ── RAF tick ───────────────────────────────────────────────────────────────
  const tick = useCallback((timestamp) => {
    const anim = animRef.current;
    if (anim.startTime === null) anim.startTime = timestamp;
    const progress = Math.min((timestamp - anim.startTime) / ANIM_DURATION, 1);
    posRef.current = anim.from + (anim.to - anim.from) * easeInOutQuart(progress);
    paint();
    if (progress < 1) {
      rafRef.current = requestAnimationFrame(tick);
    } else {
      posRef.current = anim.to;   // land exactly on integer target
      paint();
      rafRef.current    = null;
      isAnimatingRef.current  = false;
      lastWheelRef.current    = Date.now(); // start grace period after landing
    }
  }, [paint]);

  // Always start from current rendered position — interrupted moves chain smoothly
  const startAnimation = useCallback((to) => {
    isAnimatingRef.current = true;
    animRef.current = { from: posRef.current, to, startTime: null };
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  // ── navigate (wraps infinitely) ───────────────────────────────────────────
  const navigate = useCallback((idx) => {
    const n    = items.length;
    const next = ((idx % n) + n) % n;
    const prev = targetRef.current;
    if (next === prev) return;

    // Teleport posRef so the animation always moves in the intended direction.
    // Forward wrap  (last → first): slide in from below  (posRef = −1 → 0)
    // Backward wrap (first → last): slide in from above  (posRef = n  → n−1)
    if (prev === n - 1 && next === 0) posRef.current = -1;
    if (prev === 0     && next === n - 1) posRef.current = n;

    targetRef.current = next;
    setActiveIdx(next);
    onActiveChange?.(next);
    startAnimation(next);
  }, [items.length, onActiveChange, startAnimation]);

  const pauseAuto = useCallback(() => {
    isPausedRef.current = true;
    clearTimeout(pauseTimerRef.current);
    pauseTimerRef.current = setTimeout(
      () => { isPausedRef.current = false; },
      PAUSE_MS,
    );
  }, []);

  // ── imperative API exposed to HomeParallax ─────────────────────────────────
  useImperativeHandle(ref, () => ({
    goTo: (idx) => { pauseAuto(); navigate(idx); },
    scroll: (deltaY) => {
      if (isAnimatingRef.current) return;
      if (Math.abs(deltaY) < WHEEL_MIN_DELTA) return;
      const now = Date.now();
      if (now - lastWheelRef.current < WHEEL_THROTTLE_MS) return;
      lastWheelRef.current = now;
      pauseAuto();
      navigate(targetRef.current + (deltaY > 0 ? 1 : -1));
    },
  }), [navigate, pauseAuto]);

  // ── auto-rotation ──────────────────────────────────────────────────────────
  useEffect(() => {
    autoTimerRef.current = setInterval(() => {
      if (isPausedRef.current) return;
      const n    = items.length;
      const next = (targetRef.current + 1) % n;
      if (next === 0) posRef.current = -1; // forward wrap teleport
      targetRef.current = next;
      setActiveIdx(next);
      onActiveChange?.(next);
      startAnimation(next);
    }, AUTO_MS);
    return () => clearInterval(autoTimerRef.current);
  }, [items.length, onActiveChange, startAnimation]);

  // ── wheel — one step per WHEEL_THROTTLE_MS, stops propagation ─────────────
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isAnimatingRef.current) return;
      if (Math.abs(e.deltaY) < WHEEL_MIN_DELTA) return;
      const now = Date.now();
      if (now - lastWheelRef.current < WHEEL_THROTTLE_MS) return;
      lastWheelRef.current = now;
      pauseAuto();
      navigate(targetRef.current + (e.deltaY > 0 ? 1 : -1));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [navigate, items.length, pauseAuto]);

  return (
    <div
      ref={rootRef}
      style={{ position: 'relative', width: '100%', height: '100%', overflow: isMobileView ? 'visible' : 'hidden' }}
    >
      {items.map((item, i) => (
        <VisualItem
          key={item.id}
          item={item}
          isActive={i === activeIdx}
          ref={(el) => { itemRefs.current[i] = el; }}
          onClick={() => {
            if (i === activeIdx) {
              if (item.type === 'uiux') {
                routerNav(`/work/${item.id}`);
              } else {
                onOpenModal?.(item.id);
              }
            } else {
              pauseAuto();
              navigate(i);
            }
          }}
        />
      ))}
    </div>
  );
});

// ── VisualItem ────────────────────────────────────────────────────────────────
//
// Base position: top = ACTIVE_Y_RATIO (where the active card lives),
//                left = 50% (horizontal centre).
// The arc offsets (dx, dy) are applied via transform by paint() only.
//
// ⚠ `transform` and `opacity` are intentionally absent from the inline style.
//   React's reconciler resets any prop it finds in the style object on every
//   re-render. Keeping them out means paint() owns them exclusively.

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 767);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

const VisualItem = forwardRef(function VisualItem({ item, isActive, onClick }, ref) {
  const isMobile = useIsMobile();
  const imgSrc = (isMobile && item.mobileImage) ? item.mobileImage : item.image;
  const innerRef    = useRef(null);
  const prevActiveRef = useRef(false);

  useEffect(() => {
    if (window.innerWidth > 767) { prevActiveRef.current = isActive; return; }
    if (!prevActiveRef.current && isActive) {
      const el = innerRef.current;
      if (el) {
        el.classList.remove('vs-mobile-enter');
        void el.offsetWidth; // force reflow to restart animation
        el.classList.add('vs-mobile-enter');
      }
    }
    prevActiveRef.current = isActive;
  }, [isActive]);

  return (
    <div
      ref={ref}
      onClick={onClick}
      className="vs-item"
      style={{
        position:     'absolute',
        top:          `${ACTIVE_Y_RATIO * 100}%`,
        left:         `${BASE_X_RATIO * 100}%`,
        width:        `${CARD_W_RATIO * 100}%`,
        aspectRatio:  `${CARD_ASPECT}`,
        borderRadius: 15,
        overflow:     'hidden',
        cursor:       'pointer',
        willChange:   'transform, opacity',
      }}
    >
      <div ref={innerRef} className="vs-item-inner" style={{ position: 'absolute', inset: 0 }}>
        <img
          src={imgSrc}
          alt={item.title ?? ''}
          style={{
            position:       'absolute',
            inset:          0,
            width:          '100%',
            height:         '100%',
            objectFit:      'contain',
            objectPosition: 'center',
            filter:         isActive
              ? 'saturate(0.90) brightness(0.95)'
              : 'saturate(0.36) brightness(0.48)',
            transform:      isActive ? 'scale(1)' : 'scale(1.06)',
            transition:     'filter 660ms ease, transform 700ms cubic-bezier(0.34, 1.2, 0.64, 1)',
          }}
          loading="lazy"
        />
      </div>
    </div>
  );
});

export default VisualStackContainer;
